import { useCallback, useEffect, useRef, useState } from "react";
import { Platform, Pressable, Text, View } from "react-native";
import { ChevronRight, Scan } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
export type SelectedFile = {
  name: string;
  uri: string;
  mimeType: string;
  size?: number;
  objectUrl?: string;
};

type WebDropEvent = DragEvent;
type WebDropZoneTarget = View & {
  addEventListener: HTMLElement["addEventListener"];
  removeEventListener: HTMLElement["removeEventListener"];
};

export function createSelectedFileFromWebFile(file: File): SelectedFile {
  const objectUrl = URL.createObjectURL(file);
  return {
    name: file.name || "Document",
    uri: objectUrl,
    mimeType: resolveUploadContentType({
      name: file.name,
      uri: objectUrl,
      mimeType: file.type,
    }),
    size: file.size,
    objectUrl,
  };
}

function isWebDropZoneTarget(node: View | null): node is WebDropZoneTarget {
  if (!node || typeof node !== "object") return false;
  return (
    "addEventListener" in node &&
    typeof node.addEventListener === "function" &&
    "removeEventListener" in node &&
    typeof node.removeEventListener === "function"
  );
}

function getDroppedFiles(event: WebDropEvent) {
  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) return [];
  return Array.from(files);
}

export function DocumentFileDropZone({
  onPick,
  onFileSelected,
  onFilesSelected,
  compact = false,
  primaryLabel,
  secondaryLabel,
}: {
  onPick: () => void;
  onFileSelected?: (file: SelectedFile) => void;
  onFilesSelected?: (files: SelectedFile[]) => void;
  compact?: boolean;
  primaryLabel?: string;
  secondaryLabel?: string;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("DocumentFileDropZone");
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef<View | null>(null);
  const handleFilesSelected = useCallback(
    (files: SelectedFile[]) => {
      if (files.length === 0) return;
      if (onFilesSelected) {
        onFilesSelected(files);
        return;
      }
      onFileSelected?.(files[0]!);
    },
    [onFileSelected, onFilesSelected],
  );

  useEffect(() => {
    if (Platform.OS !== "web") return;

    const node = dropZoneRef.current;
    if (!isWebDropZoneTarget(node)) return;

    const handleDragEnter = (event: WebDropEvent) => {
      event.preventDefault();
      event.stopPropagation?.();
      setIsDragging(true);
    };
    const handleDragOver = (event: WebDropEvent) => {
      event.preventDefault();
      event.stopPropagation?.();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
      setIsDragging(true);
    };
    const handleDragLeave = (event: WebDropEvent) => {
      event.preventDefault();
      event.stopPropagation?.();
      setIsDragging(false);
    };
    const handleDrop = (event: WebDropEvent) => {
      event.preventDefault();
      event.stopPropagation?.();
      setIsDragging(false);
      const files = getDroppedFiles(event).map(createSelectedFileFromWebFile);
      handleFilesSelected(files);
    };

    node.addEventListener("dragenter", handleDragEnter);
    node.addEventListener("dragover", handleDragOver);
    node.addEventListener("dragleave", handleDragLeave);
    node.addEventListener("drop", handleDrop);

    return () => {
      node.removeEventListener("dragenter", handleDragEnter);
      node.removeEventListener("dragover", handleDragOver);
      node.removeEventListener("dragleave", handleDragLeave);
      node.removeEventListener("drop", handleDrop);
    };
  }, [handleFilesSelected]);

  return (
    <Pressable
      ref={dropZoneRef}
      onPress={() => {
        capturePress(
          pressLabel(
            primaryLabel ??
              (compact
                ? t("homepage.test_result_document.upload_action")
                : t("documents.upload.choose_file")),
          ),
          "documents.upload.choose_file",
        );
        onPick();
      }}
      accessibilityRole="button"
      style={{
        flexDirection: compact ? "row" : "column",
        borderWidth: 1.5,
        borderColor: isDragging
          ? "rgba(122, 62, 21, 0.42)"
          : "rgba(122, 62, 21, 0.18)",
        borderStyle: "dashed",
        borderRadius: 12,
        paddingHorizontal: compact ? 12 : 0,
        paddingVertical: compact ? 10 : 28,
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        backgroundColor: isDragging
          ? "rgba(122, 62, 21, 0.06)"
          : "rgba(0,0,0,0.02)",
      }}
    >
      <Scan
        size={compact ? 20 : 28}
        color={EMBER.colors.warm}
        strokeWidth={1.5}
      />
      <View
        style={{
          flex: compact ? 1 : undefined,
          gap: compact ? 2 : 10,
          alignItems: compact ? "flex-start" : "center",
        }}
      >
        <Text
          className="font-dm-sans"
          numberOfLines={compact ? 1 : undefined}
          style={{
            fontSize: compact ? 13 : 14,
            fontWeight: "500",
            color: EMBER.colors.text,
            textAlign: compact ? "left" : "center",
          }}
        >
          {primaryLabel ??
            (compact
              ? t("homepage.test_result_document.upload_action")
              : t("documents.upload.choose_file"))}
        </Text>
        <Text
          className="font-dm-sans"
          numberOfLines={compact ? 1 : undefined}
          style={{
            fontSize: compact ? 11 : 12,
            color: EMBER.colors.textFaint,
            textAlign: compact ? "left" : "center",
          }}
        >
          {secondaryLabel ??
            (compact
              ? t("documents.upload.choose_file")
              : t("documents.upload.file_hint"))}
        </Text>
      </View>
      {compact ? (
        <ChevronRight
          size={18}
          color={EMBER.colors.textFaint}
          strokeWidth={1.5}
        />
      ) : null}
    </Pressable>
  );
}
